import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import AuthService from "../../../../services/authService";
import { useAuth } from "../../context/AuthContext";
import Icon from "../../components/Icon";
import { Button } from "../../components/primitives";
import { carryFrom, msg } from "./authUtils";

/** Mirrors screens/EmailConfirmationScreen.js. AuthContext already polls every 3 s. */
export default function VerifyCard() {
  const { user, signOut, checkVerification } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const email = user?.email || "";
  const [note, setNote] = useState(
    location.state?.verificationEmailFailed
      ? { tone: "", text: "Email Not Sent Yet. Your account was created, but we couldn't send the verification email. Please use \"Resend email\" below to try again." }
      : null
  );
  const [resending, setResending] = useState(false);
  const [checking, setChecking] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const alive = useRef(true);
  useEffect(() => { alive.current = true; return () => { alive.current = false; }; }, []);

  useEffect(() => {
    if (cooldown <= 0) return undefined;
    const id = setInterval(() => setCooldown((c) => (c <= 1 ? 0 : c - 1)), 1000);
    return () => clearInterval(id);
  }, [cooldown > 0]); // eslint-disable-line react-hooks/exhaustive-deps

  const check = async () => {
    if (checking) return;
    setChecking(true);
    try {
      const { verified, error } = await checkVerification();
      if (!alive.current) return;
      if (error) setNote({ tone: "err", text: msg(error) });
      else if (!verified) setNote({ tone: "", text: "Not Verified Yet. Your email has not been verified yet. Please check your inbox and click the verification link." });
    } catch {
      if (alive.current) setNote({ tone: "err", text: "Failed to check verification status" });
    } finally {
      if (alive.current) setChecking(false);
    }
  };

  const resend = async () => {
    if (resending || cooldown > 0 || !email) return;
    setResending(true);
    try {
      // AuthService directly (not the context wrapper) so error.status survives for the 429 branch.
      const { error } = await AuthService.sendEmailVerification(email);
      if (!alive.current) return;
      if (error) {
        if (error.status === 429) {
          setNote({ tone: "", text: "Too Many Requests. You've sent too many emails. Please wait a few minutes before trying again." });
          setCooldown(300);
        } else {
          setNote({ tone: "", text: `Email Not Sent Yet. ${error.message || "We couldn't send the email right now. Please try again in a moment."}` });
        }
      } else {
        setNote({ tone: "ok", text: "Email Sent! Confirmation email has been resent. Please check your inbox (including spam folder)." });
        setCooldown(60);
      }
    } catch {
      if (alive.current) setNote({ tone: "err", text: "Failed to resend confirmation email. Please try again." });
    } finally {
      if (alive.current) setResending(false);
    }
  };

  const backToSignIn = async () => {
    try { await signOut(); } finally { navigate("/auth/login", { replace: true, state: carryFrom(location.state) }); }
  };

  return (
    <>
      <div className="icirc" style={{ width: 64, height: 64, borderRadius: 20 }}><Icon name="mail" size={30} /></div>
      <h2 style={{ fontSize: 28, fontWeight: 800 }}>Check your inbox</h2>
      <p className="muted">
        We sent a verification link to <b style={{ color: "var(--ink)" }}>{email}</b>. This page moves on by itself once you click it.
      </p>
      <div className="row meta"><span className="auth-pulse" />Checking every 3 seconds</div>
      {note && <div className={`auth-note ${note.tone}`.trim()} role="status">{note.text}</div>}
      <Button variant="pri" full onClick={check} disabled={checking} data-testid="verify.check">{checking ? "Checking…" : "I've verified my email"}</Button>
      <Button variant="ghost" full onClick={resend} disabled={resending || cooldown > 0} data-testid="verify.resend">
        {cooldown > 0 ? `Resend in ${cooldown}s` : resending ? "Sending…" : "Resend email"}
      </Button>
      <button type="button" className="link" onClick={backToSignIn} data-testid="verify.backToSignIn">Back to sign in</button>
    </>
  );
}
